import { IconButton, Tooltip } from "@mui/material";
import RefreshIcon from '@mui/icons-material/Refresh';
import { SelectFromItems } from "../utils/forms/select";
import { getChannelBadge } from "./channels";
import { ServerStates } from "../../utils/fgtschat/serverstates";

// Filtros ativos da lista de conversas. Os valores casam com o que o WebSocket
// espera: `channel`=chat_source, `account`=sender_number, `state`=current_state,
// `script`=script_name ('script_1'|'script_2'). `limit` limita o retorno.
export interface ChatFilters {
  channel: string;
  account: string;
  state: string;
  script: string;
  limit: number;
}

// Facets vindas do backend (janela de 24h) para montar os seletores sem hardcode.
export interface ChatFacets {
  channels: Array<{ source: string; count: number }>;
  accounts: Array<{ source: string; senderNumber: string; count: number }>;
}

export interface ChatCounts {
  total: number;       // conversas que casam com o filtro atual (24h)
  windowTotal: number; // total geral na janela de 24h
  shown: number;       // quantas voltaram (após o limite)
  limit: number;       // limite aplicado
}

export const DEFAULT_CHAT_LIMIT = 300;

const LIMIT_OPTIONS = [
  { value: '50', label: '50 conversas' },
  { value: '100', label: '100 conversas' },
  { value: '200', label: '200 conversas' },
  { value: '300', label: '300 conversas' },
  { value: '500', label: '500 conversas' },
  { value: '1000', label: '1000 conversas' },
  { value: '3000', label: 'Máximo (3000)' },
];

const SCRIPT_OPTIONS = [
  { value: 'script_1', label: 'FGTS' },
  { value: 'script_2', label: 'CLT' },
];

function channelItems(facets: ChatFacets) {
  return facets.channels.map((c) => {
    const b = getChannelBadge(c.source);
    return { value: c.source, label: `${b.icon} ${b.label} (${c.count})` };
  });
}

function accountItems(facets: ChatFacets, channel: string) {
  return facets.accounts
    .filter((a) => !channel || channel === 'all' || a.source === channel)
    .map((a) => {
      const b = getChannelBadge(a.source);
      return { value: a.senderNumber, label: `${b.icon} ${a.senderNumber} (${a.count})` };
    });
}

function stateItems(serverStates: ServerStates | undefined, script: string): string[] {
  if (!serverStates) return [];
  if (script === 'script_1') return serverStates.script1;
  if (script === 'script_2') return serverStates.script2;
  // Sem script escolhido: une os estados dos dois scripts (dedup).
  return Array.from(new Set([...serverStates.script1, ...serverStates.script2]));
}

function countText(counts: ChatCounts, filters: ChatFilters): string {
  const hasFilter = Boolean(
    (filters.channel && filters.channel !== 'all') ||
    (filters.account && filters.account !== 'all') ||
    (filters.state && filters.state !== 'all') ||
    (filters.script && filters.script !== 'all'),
  );
  let text = `${counts.windowTotal} conversa${counts.windowTotal === 1 ? '' : 's'} nas últimas 24h`;
  if (hasFilter) text += ` · ${counts.total} no filtro`;
  if (counts.shown < counts.total) text += ` · mostrando ${counts.shown} (limite ${counts.limit})`;
  return text;
}

export function ChatFilterBar({
  facets, serverStates, filters, counts, onChange, onRefresh,
}: {
  facets: ChatFacets;
  serverStates: ServerStates | undefined;
  filters: ChatFilters;
  counts: ChatCounts;
  onChange: (partial: Partial<ChatFilters>) => void;
  onRefresh: () => void;
}) {
  const cellStyle: React.CSSProperties = { flex: '1 1 180px', minWidth: '150px', maxWidth: '260px' };
  return (
    <div style={{ width: '95%', maxWidth: '1100px', margin: 'auto', marginTop: '15px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', justifyContent: 'center' }}>
        <div style={cellStyle}>
          <SelectFromItems
            label="Canal"
            defaultValue={filters.channel}
            items={channelItems(facets)}
            onChangeItem={(v) => onChange({ channel: v, account: '' })}
          />
        </div>
        <div style={cellStyle}>
          <SelectFromItems
            label="Conta / Número / Bot"
            defaultValue={filters.account}
            items={accountItems(facets, filters.channel)}
            onChangeItem={(v) => onChange({ account: v })}
          />
        </div>
        <div style={cellStyle}>
          <SelectFromItems
            label="Script"
            defaultValue={filters.script}
            items={SCRIPT_OPTIONS}
            onChangeItem={(v) => onChange({ script: v, state: '' })}
          />
        </div>
        <div style={cellStyle}>
          <SelectFromItems
            label="Estado"
            defaultValue={filters.state}
            items={stateItems(serverStates, filters.script)}
            onChangeItem={(v) => onChange({ state: v })}
          />
        </div>
        <div style={cellStyle}>
          <SelectFromItems
            label="Limite"
            defaultValue={String(filters.limit)}
            items={LIMIT_OPTIONS}
            onChangeItem={(v) => onChange({ limit: v ? parseInt(v) : DEFAULT_CHAT_LIMIT })}
          />
        </div>
        <Tooltip title="Atualizar contas e conversas">
          <IconButton onClick={onRefresh}><RefreshIcon /></IconButton>
        </Tooltip>
      </div>
      <p style={{ textAlign: 'center', marginTop: '8px', color: '#555' }}>{countText(counts, filters)}</p>
    </div>
  );
}
